/**
 * PhotoCarousel.js
 *
 * Unified photo carousel component for all card types.
 * Features:
 * - Swipeable navigation (touch support, via useSwipe)
 * - Arrow navigation on hover-capable pointers (PhotoNavArrow)
 * - Dot indicators (max 5 + ellipsis for more)
 * - Loading and error states
 * - Placeholder gradients
 * - Photo counter badge (only when the dots can't express position)
 * - Consistent aspect-[4/3] ratio
 */

import React, { Suspense, lazy, useState, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTENT_TYPES, getTypeGradient, getTypeBadge } from '../../utils/normalizeCardData';
import { CategoryIcon } from '../../utils/categoryUtils';
import { resizeForSurface } from '../../utils/imageSource';
import PhotoCredit from '../shared/PhotoCredit';
import { legacyCreditText } from '../../utils/photoCredit';
import { buildPhotoSrcSet } from '../../utils/photoSrcSet';
import useSwipe, { swipeSlideStyle, SWIPE_PRESS_STYLE } from '../../hooks/useSwipe';

import Icon from '../shared/Icon';
import ImageScrim from '../shared/ImageScrim';
import PhotoNavArrow from '../shared/PhotoNavArrow';
import PlaceholderImage from '../shared/PlaceholderImage';
import VideoPreview from '../shared/VideoPreview';

// The vertical viewer, only once a card's clip is opened (V7).
const VideoFeedViewer = lazy(() => import('../shared/VideoFeedViewer'));
const isClipSlide = (photo) => photo?.media_type === 'video';
const stop = (e) => e.stopPropagation();

// Default `sizes` for the full-width Discover feed (100vw mobile, 440px card
// column on desktop). Strip surfaces override via the sizes prop.
const DEFAULT_CARD_SIZES = '(max-width: 1024px) 100vw, 440px';

// The slide renders a photo only for these URL shapes; anything else gets the
// placeholder. Shared with the carousel-level credit, which must not credit a
// photo the slide isn't showing.
const isRenderableUrl = (url) => typeof url === 'string'
  && (url.startsWith('http') || url.startsWith('data:') || url.startsWith('/api/'));

// Dots stop at this many and the last becomes a jump-to-end. Shared with the
// carousel body, which uses it to decide whether the slide counter is telling
// the viewer anything the dots don't.
const MAX_DOTS = 5;

// Slide position and press depression both come from useSwipe
// (swipeSlideStyle / SWIPE_PRESS_STYLE): the hook writes the CSS variables and
// owns the formulas that read them, so the settle duration and the press scale
// stay identical across every dragFollow surface rather than drifting per file.

/**
 * Dot indicator component
 * Touch targets meet accessibility guidelines
 */
const DotIndicators = ({ totalSlides, currentSlide, onGoToSlide }) => {
  const { t } = useTranslation('feedCard');
  if (totalSlides <= 1) return null;

  const showEllipsis = totalSlides > MAX_DOTS;
  const dotsToShow = showEllipsis ? MAX_DOTS - 1 : totalSlides;

  // data-contrast-exempt: dots render over photos/scrim overlays that are
  // sibling layers — scripts/audit_contrast.js can't resolve the composite.
  // pointer-events-none on the row, auto on the buttons: the full-width strip
  // otherwise swallows taps meant for what lies under it (the photo credit
  // link, the action row's top edge) and turns them into card navigations.
  return (
    <div data-contrast-exempt className="absolute bottom-12 left-0 right-0 z-[3] flex items-center justify-center space-x-1.5 pointer-events-none">
      {Array.from({ length: dotsToShow }).map((_, index) => (
        <button
          key={index}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onGoToSlide(index); }}
          className="pointer-events-auto min-w-[24px] min-h-[24px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-inverse/50 focus-visible:ring-offset-1 rounded-full"
          aria-label={t('carousel.dot.goToSlide', { number: index + 1 })}
        >
          <span className={`block rounded-full transition-all duration-normal ${
            currentSlide === index
              ? 'bg-content-inverse w-4 h-2'
              : 'bg-content-inverse/50 hover:bg-content-inverse/70 w-2 h-2'
          }`} />
        </button>
      ))}
      {showEllipsis && (
        <>
          <span className="text-content-inverse/60 text-xs">{t('carousel.dot.ellipsis')}</span>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onGoToSlide(totalSlides - 1); }}
            className="pointer-events-auto min-w-[24px] min-h-[24px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content-inverse/50 focus-visible:ring-offset-1 rounded-full"
            aria-label={t('carousel.dot.goToLastSlide')}
          >
            <span className={`block rounded-full transition-all duration-normal ${
              currentSlide === totalSlides - 1
                ? 'bg-content-inverse w-4 h-2'
                : 'bg-content-inverse/50 hover:bg-content-inverse/70 w-2 h-2'
            }`} />
          </button>
        </>
      )}
    </div>
  );
};

/**
 * Loading spinner overlay
 */
// eslint-disable-next-line no-unused-vars
const LoadingOverlay = ({ gradient }) => {
  const { t } = useTranslation('feedCard');
  return (
    // Flat since the 2026-10-01 refresh; `gradient` is still passed, unused
    <div className="w-full h-full flex items-center justify-center bg-surface-tertiary">
      <div className="text-center text-content-secondary">
        <div className="animate-spin w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full mx-auto mb-2" />
        <div className="text-sm opacity-90">{t('carousel.loading')}</div>
      </div>
    </div>
  );
};

/**
 * Placeholder image for items without photos
 */
// PlaceholderImage is now the shared no-photo fallback (imported above).

/**
 * Single slide in the carousel
 */
const CarouselSlide = ({
  photo,
  index,
  currentSlide,
  totalPhotos,
  type,
  showOverlay = true,
  remainingCount = 0,
  onViewMore,
  showCounter = true,
  surface = 'large',
  sizes = DEFAULT_CARD_SIZES,
  onImageError,
  onOpenVideo,
  videoOpen = false
}) => {
  const { t } = useTranslation('feedCard');
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Only fetch images within a ±1 window of the current slide. The current slide
  // is what the user sees; the neighbours are prefetched so swiping in either
  // direction feels instant. Slides further out stay as the gradient placeholder
  // until the window reaches them — this is the main bandwidth saver, since most
  // users never swipe past the first couple of photos on a feed card. Once a slide
  // has entered the window we keep it mounted (hasEntered) so swiping back doesn't
  // refetch an already-loaded image.
  const inWindow = Math.abs(index - currentSlide) <= 1;
  const hasEnteredRef = useRef(false);
  if (inWindow) hasEnteredRef.current = true;
  const shouldRenderImage = inWindow || hasEnteredRef.current;

  const hasValidUrl = isRenderableUrl(photo?.url);

  // A clip (V7): its poster, the muted loop while it's the slide on screen of
  // the card nearest the centre (VideoPreview's own coordinator), and a play
  // button for the vertical viewer with sound. Never HLS on a card.
  if (isClipSlide(photo) && photo.playback) {
    return (
      <div className="absolute inset-0 transition-transform ease-out" style={swipeSlideStyle(index, currentSlide)}>
        {shouldRenderImage && (
          // Stopped while its clip plays in the viewer: two videos at once otherwise.
          <VideoPreview playback={photo.playback} enabled={index === currentSlide && !videoOpen} className="absolute inset-0 w-full h-full" />
        )}
        {onOpenVideo && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenVideo(photo); }}
            aria-label={t('carousel.playVideoLabel')}
            className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-black/50 text-content-inverse flex items-center justify-center z-[1] focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2"
          >
            <Icon name="play" className="w-6 h-6" />
          </button>
        )}
        {showCounter && (
          <div className="absolute top-3 right-3 bg-black/50 text-content-inverse text-xs px-2 py-1 rounded-full z-[2]">
            {t('carousel.slideCounter', { current: index + 1, total: totalPhotos })}
          </div>
        )}
      </div>
    );
  }

  if (!hasValidUrl || imageError) {
    return (
      <div
        className="absolute inset-0 transition-transform ease-out"
        style={swipeSlideStyle(index, currentSlide)}
      >
        <div className="w-full h-full bg-primary-lighter flex items-center justify-center">
          <div className="text-center text-primary-dark">
            <div className="text-5xl mb-2">
              {photo?.category ? <CategoryIcon category={photo.category} className="w-10 h-10 text-primary-dark" /> : <Icon name={getTypeBadge(type)?.icon || 'mapPin'} className="w-10 h-10" />}
            </div>
            <div className="text-lg font-medium opacity-90 px-4 truncate max-w-[200px]">
              {photo?.title || t('carousel.itemFallback')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute inset-0 transition-transform ease-out"
      style={swipeSlideStyle(index, currentSlide)}
    >
      {/* Image — only mounted once the slide is within the ±1 window so out-of-view
          slides don't trigger image downloads. */}
      {(!imageLoaded || !shouldRenderImage) && (
        <div className="absolute inset-0 bg-surface-tertiary animate-pulse" />
      )}
      {shouldRenderImage && (
        <img
          src={resizeForSurface(photo.url, surface)}
          srcSet={buildPhotoSrcSet(photo.url) || undefined}
          sizes={buildPhotoSrcSet(photo.url) ? sizes : undefined}
          alt={photo.title || t('carousel.photoAltFallback', { number: index + 1 })}
          loading="lazy"
          className={`w-full h-full object-cover transition-opacity duration-normal ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
          onError={() => { setImageError(true); onImageError?.(photo.url); }}
        />
      )}

      {/* Info overlay - top of photo for collections/boards */}
      {showOverlay && photo.title && type !== CONTENT_TYPES.ACTIVITY && (
        <>
        <ImageScrim position="top" className="z-[1]" />
        <div className="absolute top-0 inset-x-0 p-3 pr-20 z-[1]">
          <div className="flex items-center space-x-2">
            {photo.contentType && photo.contentType !== 'activity' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-content-inverse/20 text-content-inverse capitalize">{photo.contentType}</span>
            )}
            <span className="text-content-inverse font-semibold truncate text-sm">
              {photo.title}
            </span>
          </div>
          <div className="flex items-center space-x-2 text-content-inverse/80 text-xs mt-0.5 ml-0.5">
            {photo.rating && (
              <span className="flex items-center">
                <Icon name="star" className="w-3 h-3 text-accent mr-1" />
                {photo.rating.toFixed(1)}
              </span>
            )}
            {photo.location && (
              <span className="truncate max-w-[150px]">
                {photo.location}
              </span>
            )}
          </div>
        </div>
        </>
      )}

      {/* Slide counter — only where the dot row can't carry position on its
          own. Under MAX_DOTS the dots already say both "where am I" and "how
          many", so the pill was a third piece of chrome repeating them over a
          photo that has an overlay title and arrows to accommodate too. */}
      {showCounter && (
        <div className="absolute top-3 right-3 bg-black/50 text-content-inverse text-xs px-2 py-1 rounded-full z-[2]">
          {t('carousel.slideCounter', { current: index + 1, total: totalPhotos })}
        </div>
      )}

      {/* "More items" overlay on last displayed photo */}
      {!!remainingCount && (
        <div
          className="absolute inset-0 bg-black/40 flex items-center justify-center z-[1] cursor-pointer"
          onClick={(e) => { e.stopPropagation(); onViewMore?.(); }}
        >
          <div className="text-center text-content-inverse">
            <div className="text-2xl font-bold mb-1">{t('carousel.moreCount', { count: remainingCount })}</div>
            <div className="inline-flex items-center gap-0.5 text-sm font-medium opacity-90">
              {t('carousel.viewMoreLabel', { label: getTypeBadge(type)?.label || t('carousel.allFallback') })}
              <Icon name="chevronRight" className="w-4 h-4 flex-shrink-0" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * "View More" teaser slide for collections/boards with many items
 */
const MoreSlide = ({ remainingCount, type, onViewAll }) => {
  const { t } = useTranslation('feedCard');
  const badge = getTypeBadge(type);

  return (
    <div
      className="w-full h-full bg-primary-lighter flex items-center justify-center cursor-pointer"
      onClick={onViewAll}
    >
      <div className="text-center text-primary-dark px-6">
        <div className="mb-3"><Icon name={badge?.icon || 'bookOpen'} className="w-10 h-10" /></div>
        <div className="text-2xl font-bold mb-2">{t('carousel.moreCount', { count: remainingCount })}</div>
        <div className="text-sm opacity-90 mb-4">
          {type === CONTENT_TYPES.BOARD ? t('carousel.moreSlide.subtitleBoard') : t('carousel.moreSlide.subtitleDefault')}
        </div>
        <button
          className="px-6 py-2.5 min-h-[44px] bg-primary text-content-inverse rounded-full font-semibold text-sm hover:bg-primary-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
        >
          {t('carousel.moreSlide.viewAllButton')}
        </button>
      </div>
    </div>
  );
};

/**
 * Main PhotoCarousel Component
 */
const PhotoCarousel = ({
  carousel,
  type,
  title,
  badge: badgeProp,
  isLoading = false,
  onViewAll,
  showMoreSlide = false,
  maxPreviewItems = 6,
  className = '',
  // Surface-decides-size (see project photo model): defaults are tuned for
  // the full-width Discover feed. Narrow surfaces (Home's 288px card strips)
  // pass smaller values so retina devices don't pull 1200w candidates into
  // 288px slots.
  surface = 'large',
  sizes = DEFAULT_CARD_SIZES
}) => {
  const { t } = useTranslation('feedCard');
  const [currentSlide, setCurrentSlide] = useState(0);
  // The cover URL whose image failed, so the Google credit doesn't sit on the
  // placeholder that replaces it. Keyed by URL: a new cover starts clean.
  const [failedCover, setFailedCover] = useState(null);
  // Slide URLs whose image failed to load (the slide shows its placeholder).
  const [failedSlideUrls, setFailedSlideUrls] = useState(() => new Set());
  const markSlideFailed = useCallback((url) => {
    setFailedSlideUrls(prev => (prev.has(url) ? prev : new Set(prev).add(url)));
  }, []);
  // The card's clip opened in the vertical viewer (its id), with the card's
  // other clips a swipe away.
  const [openClipId, setOpenClipId] = useState(null);
  const openVideo = useCallback((slide) => setOpenClipId(slide.id), []);

  const { photos, coverPhoto, photoAttribution, totalPhotos } = carousel || {};

  // Calculate slide counts
  const displayPhotos = photos?.slice(0, maxPreviewItems) || [];
  const hasMoreItems = totalPhotos > maxPreviewItems && showMoreSlide;
  const remainingItems = totalPhotos - displayPhotos.length;
  const totalSlides = hasMoreItems ? displayPhotos.length + 1 : displayPhotos.length;

  // The photo set can shrink between renders — a feed refetch swaps the item
  // while the card instance (keyed by content id) survives, e.g. after a photo
  // is deleted, re-tiered, or un-approved. A currentSlide beyond the new end
  // would translate every remaining slide off-screen and blank the card, so
  // reconcile it here (the render-time state-adjustment pattern; the condition
  // only fires out-of-range, so it cannot loop).
  if (currentSlide > 0 && currentSlide > totalSlides - 1) {
    setCurrentSlide(Math.max(totalSlides - 1, 0));
  }
  // The same refetch can drop the clip the viewer was opened on: close it
  // rather than leave a viewer with nothing to show still holding the scroll
  // lock (or reopen unasked when the clip comes back).
  const clipItems = displayPhotos.filter(isClipSlide).map(p => p.video).filter(Boolean);
  if (openClipId != null && !clipItems.some(v => v.id === openClipId)) {
    setOpenClipId(null);
  }

  // Navigation
  const goToSlide = useCallback((index) => {
    setCurrentSlide(Math.max(0, Math.min(index, totalSlides - 1)));
  }, [totalSlides]);

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  const swipeHandlers = useSwipe({
    onSwipeLeft: nextSlide,
    onSwipeRight: prevSlide,
    enabled: totalSlides > 1,
    // Slides render translateX with --swipe-x/--swipe-snap-ms (see slideStyle),
    // so the photo tracks the finger and depresses under it, YouTube-style.
    dragFollow: true,
    canSwipeLeft: currentSlide < totalSlides - 1,
    canSwipeRight: currentSlide > 0
  });

  const gradient = getTypeGradient(type);
  const badge = badgeProp || getTypeBadge(type);

  // Every branch spreads the swipe handlers, not just the full carousel.
  // React reuses the same DOM node when these branches flip (same position,
  // same element type), so a refetch landing mid-drag would otherwise strip
  // the handlers off a node still carrying live --swipe-* properties, with no
  // touchend able to reach the hook to clear them. Disabled handlers are
  // no-ops with an undefined style, so the extra spread costs nothing.

  // Loading state
  if (isLoading) {
    return (
      <div className={`relative aspect-[4/3] overflow-hidden bg-surface-secondary ${className}`} {...swipeHandlers}>
        <LoadingOverlay gradient={gradient} />
      </div>
    );
  }

  // No photos - show placeholder
  if (!displayPhotos.length && !coverPhoto) {
    return (
      <div className={`relative aspect-[4/3] overflow-hidden bg-surface-secondary ${className}`} {...swipeHandlers}>
        <PlaceholderImage type={type} title={title} badge={badge} gradient={gradient} />
      </div>
    );
  }

  // Single photo or coverPhoto only - no carousel needed
  if (displayPhotos.length === 0 && coverPhoto) {
    return (
      <div className={`relative aspect-[4/3] overflow-hidden bg-surface-secondary ${className}`} {...swipeHandlers}>
        {/* Keyed by the cover (the root must stay the same node — see above):
            onError hides the <img> and shows the placeholder by mutating the
            DOM, so a new cover needs fresh elements to start visible. */}
        <React.Fragment key={coverPhoto}>
        <img
          src={resizeForSurface(coverPhoto, surface)}
          srcSet={buildPhotoSrcSet(coverPhoto) || undefined}
          sizes={buildPhotoSrcSet(coverPhoto) ? sizes : undefined}
          alt={title || t('carousel.coverAltFallback')}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
            setFailedCover(coverPhoto);
          }}
        />
        <div className="hidden">
          <PlaceholderImage type={type} title={title} badge={badge} gradient={gradient} />
        </div>
        </React.Fragment>
        {/* Above the ActionRow overlay (z-[3] over its z-[2] gradient and
            buttons), which used to paint over a bottom-1 credit and swallow its
            taps. */}
        {failedCover !== coverPhoto && (
          <PhotoCredit
            src={coverPhoto}
            size="md"
            position="inline"
            fallbackText={legacyCreditText(photoAttribution)
              || (photoAttribution?.displayName ? t('carousel.photoByFallback', { displayName: photoAttribution.displayName }) : null)}
            className="absolute bottom-[3.25rem] inset-x-3 z-[3] text-content-inverse"
          />
        )}
      </div>
    );
  }

  // Full carousel
  return (
    <div
      className={`relative group aspect-[4/3] overflow-hidden bg-surface-secondary ${className}`}
      {...swipeHandlers}
    >
      {/* Slides, inside the press-depression wrapper. The transform must not
          sit on the root above: it would make the root a stacking context and
          collapse the z-[3] dot row into one atom that UnifiedCard's later
          z-[2] bottom gradient paints over. See SWIPE_PRESS_STYLE. */}
      <div className="absolute inset-0" style={SWIPE_PRESS_STYLE}>
        {displayPhotos.map((photo, index) => (
          <CarouselSlide
            key={photo.id || index}
            photo={photo}
            index={index}
            currentSlide={currentSlide}
            totalPhotos={totalPhotos}
            type={type}
            remainingCount={index === displayPhotos.length - 1 ? remainingItems : 0}
            onImageError={markSlideFailed}
            onOpenVideo={openVideo}
            videoOpen={!!openClipId}
            onViewMore={onViewAll}
            showCounter={totalSlides > MAX_DOTS}
            surface={surface}
            sizes={sizes}
          />
        ))}

        {/* "More" teaser slide */}
        {hasMoreItems && (
          <div
            className="absolute inset-0 transition-transform ease-out"
            style={swipeSlideStyle(displayPhotos.length, currentSlide)}
          >
            <MoreSlide
              remainingCount={remainingItems}
              type={type}
              onViewAll={onViewAll}
            />
          </div>
        )}
      </div>

      {/* Navigation arrows — pointer devices only; touch swipes (see the
          container's swipe handlers) and every set here also has dots. */}
      {totalSlides > 1 && (
        <>
          <PhotoNavArrow
            direction="left"
            onClick={prevSlide}
            disabled={currentSlide === 0}
            label={t('carousel.arrow.previousLabel')}
          />
          <PhotoNavArrow
            direction="right"
            onClick={nextSlide}
            disabled={currentSlide === totalSlides - 1}
            label={t('carousel.arrow.nextLabel')}
          />
        </>
      )}

      {/* Dot indicators - overlaid on photo above action row */}
      {totalSlides > 1 && (
        <DotIndicators
          totalSlides={totalSlides}
          currentSlide={currentSlide}
          onGoToSlide={goToSlide}
        />
      )}

      {/* Photo credit for the slide on screen, every source
          (docs/WIKIMEDIA_ATTRIBUTION.md), looked up from that slide's URL.
          Drawn here, not inside the slide: each slide is transformed, so a
          credit inside one is trapped under UnifiedCard's z-[2] ActionRow
          gradient, which greyed it out (QA 2026-10-13). z-[3] puts it with the
          dots, above the gradient. bottom-[4.75rem] clears the dot row
          (bottom-12, 24px targets) when there is one, bottom-[3.25rem] the
          ActionRow's buttons otherwise; can-hover:inset-x-14 clears the
          paging arrows, which render only where a pointer can hover. None
          over a slide showing its placeholder (bad URL, or the image failed —
          a deleted Commons file 404s). */}
      {currentSlide < displayPhotos.length
        && !isClipSlide(displayPhotos[currentSlide])
        && isRenderableUrl(displayPhotos[currentSlide]?.url)
        && !failedSlideUrls.has(displayPhotos[currentSlide].url) && (
        <PhotoCredit
          key={displayPhotos[currentSlide]?.url}
          src={displayPhotos[currentSlide]?.url}
          size="md"
          position="inline"
          fallbackText={legacyCreditText(displayPhotos[currentSlide]?.attribution)}
          className={`absolute ${totalSlides > 1 ? 'bottom-[4.75rem]' : 'bottom-[3.25rem]'} inset-x-3 can-hover:inset-x-14 z-[3] text-content-inverse`}
        />
      )}
      {openClipId && (
        // The viewer portals out, but React bubbles its touches up to this
        // carousel's swipe handlers: keep them here (clicks and keys are the
        // card's to ignore, UnifiedCard; the viewer's Escape is a document listener).
        <div onTouchStart={stop} onTouchMove={stop} onTouchEnd={stop} onPointerDown={stop}>
        <Suspense fallback={null}>
          <VideoFeedViewer
            items={clipItems}
            startId={openClipId}
            onClose={() => setOpenClipId(null)}
          />
        </Suspense>
        </div>
      )}
    </div>
  );
};

export default memo(PhotoCarousel);
